import { LoaderFunctionArgs, redirect } from "react-router-dom";

import { getDatefromWeekNum0, myAxios, MyDate, weeknum } from "src/components/common/functions";

const allowedSearchKeys = ["view", "date"] as const;
const allowedViews = new Set(["month", "week", "day"]);

export async function payrollLoader({ params, request }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    let shouldRedirect = false;

    for (const [key] of url.searchParams) {
        if (!allowedSearchKeys.includes(key as typeof allowedSearchKeys[number])) {
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }

    const fallbackWeek = params.week ?? weeknum(new MyDate());
    const urlView = url.searchParams.get("view") ?? "week";
    const view = allowedViews.has(urlView) ? urlView : "week";
    if (urlView !== view) {
        url.searchParams.set("view", view);
        shouldRedirect = true;
    }

    const fallbackDate = getDatefromWeekNum0(fallbackWeek, 0)?.toLocaleDateString("fr-CA", { timeZone: "America/Los_Angeles" }) ?? new MyDate().s();
    const requestedDate = normalizeDate(url.searchParams.get("date")) ?? fallbackDate;
    const normalizedWeek = weeknum(requestedDate);
    const activeDate = requestedDate;

    if (url.searchParams.get("date") !== activeDate) {
        url.searchParams.set("date", activeDate);
        shouldRedirect = true;
    }

    if (!params.week || params.week !== normalizedWeek || shouldRedirect) {
        throw redirect(`/Payroll/${normalizedWeek}?${url.searchParams.toString()}`);
    }

    const { weekStart, weekEnd } = getRangeFromView(view, activeDate, normalizedWeek);

    const formData = new FormData();
    formData.append("week", normalizedWeek);
    formData.append("weekStart", weekStart);
    formData.append("weekEnd", weekEnd);

    const res = await myAxios({ fileName: "payroll/readPayroll.php", formData });
    if (!res) throw redirect("..");
    const weeks: string[] = [];
    const minTime = res.data.minTime;
    const maxTime = res.data.maxTime;
    if (minTime && maxTime) {
        const dateMin = new Date(String(minTime).replace(/-/g, "/"));
        const dateMax = new Date(String(maxTime).replace(/-/g, "/"));
        while (dateMin < dateMax) {
            weeks.push(weeknum(dateMin));
            dateMin.setDate(dateMin.getDate() + 7);
        }
        if (!weeks.includes(weeknum(dateMax))) {
            weeks.push(weeknum(dateMax));
        }
    }

    const sortedWeeks = weeks.sort((a, b) => (a < b ? 1 : -1));
    if (sortedWeeks.length && !sortedWeeks.includes(normalizedWeek)) {
        const nextWeek = sortedWeeks[0];
        const nextDate = getDatefromWeekNum0(nextWeek, 0)?.toLocaleDateString("fr-CA", { timeZone: "America/Los_Angeles" }) ?? activeDate;
        url.searchParams.set("date", nextDate);
        url.searchParams.set("view", view);
        throw redirect(`/Payroll/${nextWeek}?${url.searchParams.toString()}`);
    }

    return {
        ...res.data,
        weeks: sortedWeeks,
        week: normalizedWeek,
        activeDate,
        view,
    };
}

function normalizeDate(value: string | null) {
    if (!value) return null;
    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

function getRangeFromView(view: string, activeDate: string, normalizedWeek: string) {
    if (view === "day") {
        const date = new MyDate(activeDate.replace(/-/g, "/"));
        return {
            weekStart: date.s(),
            weekEnd: new MyDate(date.getFullYear(), date.getMonth(), date.getDate() + 1).s(),
        };
    }
    if (view === "month") {
        const date = new MyDate(activeDate.replace(/-/g, "/"));
        return {
            weekStart: new MyDate(date.getFullYear(), date.getMonth(), 1).s(),
            weekEnd: new MyDate(date.getFullYear(), date.getMonth() + 1, 1).s(),
        };
    }
    return {
        weekStart: getDatefromWeekNum0(normalizedWeek, 0)!.toLocaleDateString("fr-CA", { timeZone: "America/Los_Angeles" }),
        weekEnd: getDatefromWeekNum0(normalizedWeek, 7)!.toLocaleDateString("fr-CA", { timeZone: "America/Los_Angeles" }),
    };
}
